import { Alert, Badge, Box, Button, Group, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconPlayerPlay } from '@tabler/icons-react'
import { useState } from 'react'

import { baixarGravacao } from '@/api/ssmGravacoes'

import { ReprodutorDeSessao } from './ReprodutorDeSessao'

/**
 * O que um evento `ssm_session:*` mostra além do payload cru.
 *
 * A trilha é a porta de entrada para as gravações (decisão do Carlos em
 * 01/09/2026), e o motivo é concreto: **recusa não gera gravação**. Uma tela
 * "de gravações" nunca mostraria a tentativa barrada — que é justamente o que
 * uma auditoria procura.
 */
export function SessaoSsm({
  payload,
  acao,
  tenantId,
}: {
  payload: Record<string, unknown>
  acao: string
  tenantId: number
}) {
  const [aberto, setAberto] = useState(false)

  const instancia = String(payload.instance_id ?? '—')
  const ambiente = String(payload.ambiente ?? '—')
  const gravacaoId = typeof payload.recording_id === 'number' ? payload.recording_id : null
  const duracao = typeof payload.duracao_seg === 'number' ? payload.duracao_seg : null
  const bytes = typeof payload.bytes_gravados === 'number' ? payload.bytes_gravados : null
  const motivo = typeof payload.motivo === 'string' ? payload.motivo : null

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Badge variant="light" color={corDoAmbiente(ambiente)}>{ambiente}</Badge>
        <Text size="sm" ff="monospace">{instancia}</Text>
        {duracao !== null && <Text size="sm" c="dimmed">{formatarDuracao(duracao)}</Text>}
        {bytes !== null && <Text size="sm" c="dimmed">{formatarBytes(bytes)} gravados</Text>}
      </Group>

      {motivo && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text size="sm">Recusada: {explicarMotivo(motivo)}</Text>
        </Alert>
      )}

      {acao === 'ssm_session:denied' && (
        <Text size="xs" c="dimmed">
          Tentativa recusada não gera gravação — não há o que reproduzir.
        </Text>
      )}

      {gravacaoId !== null && bytes === 0 && (
        <Text size="xs" c="dimmed">
          A sessão abriu e nada foi digitado nem devolvido: a gravação está vazia.
        </Text>
      )}

      {gravacaoId !== null && bytes !== null && bytes > 0 && !aberto && (
        <Group>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={() => setAberto(true)}
          >
            Ver gravação
          </Button>
          <Text size="xs" c="dimmed">
            Gravações são apagadas 90 dias depois da sessão.
          </Text>
        </Group>
      )}

      {aberto && gravacaoId !== null && (
        <ReprodutorDeSessao
          carregar={() => baixarGravacao(tenantId, gravacaoId)}
          onFechar={() => setAberto(false)}
        />
      )}
    </Stack>
  )
}

/** Mesma paleta da lista de servidores, para o ambiente ser reconhecível. */
function corDoAmbiente(ambiente: string): string {
  const cores: Record<string, string> = {
    PRD: 'red', HML: 'orange', QA: 'blue', DEV: 'gray',
  }
  return cores[ambiente.toUpperCase()] ?? 'gray'
}

/**
 * Traduz o eixo que reprovou.
 *
 * O motivo chega como `politica:mfa_recency` — legível para quem escreveu o
 * `policy_engine` e opaco para quem audita. E o de MFA é o que mais engana:
 * parece falta de permissão, e não é.
 */
export function explicarMotivo(motivo: string): string {
  const eixo = motivo.startsWith('politica:') ? motivo.slice('politica:'.length) : motivo
  const textos: Record<string, string> = {
    mfa_recency: 'verificação de identidade vencida para este ambiente — não é falta de permissão',
    rbac: 'sem permissão de shell neste ambiente',
    time_window: 'fora da janela de horário permitida',
    ip_allowlist: 'endereço de rede fora da lista permitida',
    app_visibility: 'o perfil não alcança este servidor',
    environment_scope: 'o perfil não alcança este ambiente',
  }
  return textos[eixo] ?? motivo
}

export function formatarDuracao(segundos: number): string {
  if (segundos < 60) return `${segundos}s`
  const m = Math.floor(segundos / 60)
  const s = segundos % 60
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

export function formatarBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
